/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CollectionIntegrityPanel.tsx
 *     Feature         :   265 - Collection Integrity · US5 / FR-025 to FR-030
 *     Description     :   The integrity view shared by Collection Jobs and Infrastructure
 *                         Sync: per-unit completeness, classified problems with evidence-
 *                         based remediation, and an affirmative healthy state.
 *
 *  🛑 THE ONE DISTINCTION THIS PANEL EXISTS TO MAKE (FR-025):
 *
 *     "could not be collected" is NOT "does not exist". An indeterminate coverage renders
 *     as `NotCollected` WITH its reason — never as 0%, never as a blank cell. Collapsing
 *     the two is the defect the whole spec was written against: on 2026-07-29, 678 runs
 *     recorded a flawless day while authenticating to nothing, and every screen agreed.
 *
 *  🛑 EVERY PERCENTAGE NAMES ITS UNIT. The panel once printed `100.0% — 608 of 608
 *     succeeded · Failed units 0` directly above `auth: 683`, because the headline came
 *     from the primary tally while the cause list merged every tally. Both halves were
 *     correct; the pair was unreadable, and a reader who cannot tell which number to
 *     believe believes neither. A percentage is now always stamped with the unit it
 *     measures, and every other unit is shown beside it with its own arithmetic.
 *
 *  🛑 HEALTH IS SHOWN AFFIRMATIVELY (FR-003b). The spec forbids a success ALERT — a
 *     healthy day is ~678 runs — but absence of red must not be the only evidence of
 *     health, or "nothing wrong" keeps meaning both "fine" and "nothing ran".
 *
 *     Kit + tokens only (Constitution XLV). No hand-rolled cards, tables or badges.
 *     Constitution    :   v1.24.0 - XLV (design blueprint), XLII
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-31
 * =========================================================================================
 */

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, ShieldAlert, Terminal } from 'lucide-react';
import {
  Badge,
  Card,
  DataTable,
  DataTableColumn,
  DescriptionList,
  EmptyState,
  Modal,
  NotCollected,
  StatCard,
} from '@/components/ui';
import { fixRecipeFor, type FixRecipe } from './collectionFixRecipes';

/** One unit of work within a stage, carrying its own denominator and its own causes. */
export interface StageUnit {
  unit: string;
  isPrimary: boolean;
  attempted: number;
  succeeded: number;
  failed: number;
  truncated: number;
  unaccounted: number;
  pct: number | null;
  worklistIncomplete?: boolean;
  worklistIncompleteReason?: string | null;
  reasons: Record<string, number>;
  samples?: Record<string, string[]>;
}

export interface StageCompleteness {
  stage: string;
  attemptedPct: number | null;
  expectedPct: number | null;
  expectedState: string;
  indeterminateReason: string | null;
  disposition: string;
  attemptedTotal: number | null;
  succeededTotal: number | null;
  failedTotal?: number | null;
  /** Causes for the PRIMARY unit only — matches `failedTotal`. */
  failedByReason?: Record<string, number> | null;
  /** The unit the headline figures are counted in. */
  primaryUnit?: string | null;
  units?: StageUnit[];
  /** Failures summed across every unit — the honest total behind a clean headline. */
  failedAcrossUnits?: number;
  runStatus?: string | null;
  /** The stage is running again: its figures are the last finished run's. */
  runningSince?: string | null;
  degradedReason?: string | null;
  recordedAt: string;
}

export interface CollectionProblem {
  id: string;
  stage: string;
  causeCategory: string;
  affectedScope: string;
  identityRef: string;
  classification: string;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Verbatim (scrubbed) error text — the only thing that can say WHY. */
  errorSamples?: string[];
}

export interface CleanStreak {
  stage: string;
  streak: number;
  lastCleanAt: string | null;
}

export interface CollectionIntegrityPanelProps {
  completeness: StageCompleteness[];
  problems: CollectionProblem[];
  /** Spec 286 T159 item 29: the open problems' true counts over the same scope. `problems` holds the first 100 of
   *  them, so its length is not a total. */
  problemCounts?: { total: number; permission: number; stages: number };
  streaks?: CleanStreak[];
  /** Called when an operator opens a problem's stored diagnostic samples (FR-030). */
  onInspectProblem?: (problemId: string) => void;
}

/** Human label for an internal unit name. */
const unitLabel = (unit: string | null | undefined, n?: number): string => {
  const base = (unit ?? 'item').replace(/_/g, ' ');
  if (n === undefined || n === 1) return base;
  return base.endsWith('s') ? base : `${base}s`;
};

/** `1 unit` / `2 units`. Bare `${n} units` printed "1 other units" on a real screen. */
const plural = (n: number, word: string): string => `${n} ${n === 1 ? word : `${word}s`}`;

/** The runnable fix for a problem — evidence first, category second. */
export const recipeFor = (p: CollectionProblem, environment?: string): FixRecipe =>
  fixRecipeFor({
    stage: p.stage,
    causeCategory: p.causeCategory,
    affectedScope: p.affectedScope,
    identityRef: p.identityRef,
    errorSamples: p.errorSamples,
    environment,
  });

/**
 * Remediation is SPECIFIC or it is not remediation (FR-027/028).
 *
 * 🛑 The client cannot change our code — only their tenant. A remedy they cannot execute
 *    is indistinguishable from no remedy, which is how a missing grant survives for
 *    months while every screen reports a healthy platform.
 */
export function remediationFor(p: CollectionProblem, environment?: string): string {
  const r = recipeFor(p, environment);
  return `${r.verdict} ${r.why}`;
}

/**
 * Age of the most recent occurrence, in words.
 *
 * 🛑 The answer to "is this old or new?", which a bare occurrence count cannot give.
 *    Problems only clear when their collector runs again without reproducing them, so
 *    a stale timestamp means "not seen since" — NOT "still happening".
 */
function relativeAge(iso: string | null | undefined): string {
  if (!iso) return 'never seen';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown age';
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const severityForCause = (cause: string): 'error' | 'warning' | 'info' =>
  cause === 'auth' || cause === 'authorization' ? 'error' : cause === 'unknown' ? 'info' : 'warning';

/**
 * Colour for a stage outcome — ONE definition, used by the table and the detail alike.
 *
 * 🛑 `uninstrumented` must not be amber. Amber says "something went wrong", and nothing
 *    did: the stage ran and counted nothing, so there is no failure to look for. Neutral
 *    says "no claim available", which is the true state and the one that prompts the right
 *    next action — add a counter, rather than hunt an outage. Green would be worse still:
 *    that is the exact inversion this feature was written to remove.
 */
const dispositionVariant = (d: string): 'success' | 'warning' | 'info' =>
  d === 'full' ? 'success' : d === 'degraded' ? 'warning' : 'info';

/** Condense a stored reason into one readable line. */
function shortWhy(reason: string | null): string {
  if (!reason) return 'No denominator exists for this stage.';
  if (reason.includes('merge stage')) return 'Measured by the merge stage only.';
  return reason.length > 80 ? `${reason.slice(0, 77)}…` : reason;
}

/**
 * What is missing before this stage could report estate coverage (FR-025).
 *
 * 🛑 "not collected" without this is a dead end. The operator's next question is always
 *    "what would it take?", and the answer is knowable — so it is stated.
 */
function whatIsMissing(row: StageCompleteness): string {
  if (row.expectedState !== 'indeterminate') return 'Nothing — estate coverage is measured for this stage.';
  if ((row.indeterminateReason ?? '').includes('merge stage')) {
    return (
      'Only the merge step measures how much of your estate a stage covered. These figures ' +
      'show how much of what this stage tried succeeded, not how much of the estate it reached.'
    );
  }
  return (
    'This stage enumerates a remote estate, so it cannot know what it was never shown. ' +
    'A second, independent source of truth is required to establish the denominator — ' +
    'which is what the billing cross-check on Infrastructure Sync provides.'
  );
}

/** Units other than the primary that recorded any failure. */
const failingSecondaryUnits = (row: StageCompleteness): StageUnit[] =>
  (row.units ?? []).filter((u) => !u.isPrimary && u.failed > 0);

/**
 * The fix, rendered so it can be executed rather than interpreted.
 *
 * 🛑 Commands are shown verbatim and monospaced with the placeholders visible. A recipe
 *    that silently substitutes a guessed object id would be run, fail confusingly, and
 *    cost more trust than admitting the value is unknown.
 */
const FixBlock: React.FC<{ recipe: FixRecipe }> = ({ recipe }) => (
  <div className="space-y-3">
    <div>
      <p className="text-sm font-medium text-[var(--color-text-primary)]">{recipe.verdict}</p>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{recipe.why}</p>
    </div>

    {/* 🛑 A FAULT THAT IS OURS IS SAID TO BE OURS, FIRST. Detail and commands are for
        things the customer can change in their own tenant; everything else — a collector
        defect, a stale work-list, throttling, an unrecognised message — is escalated.
        Showing remediation steps for our own bug invites someone to go and alter working
        infrastructure, which is the most expensive kind of wrong advice because it looks
        like progress. */}
    {recipe.audience !== 'client' && (
      <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
        <p className="text-sm font-medium text-[var(--color-text-primary)]">
          {recipe.audience === 'informational'
            ? 'Nothing to do — this clears on its own'
            : 'Nothing to change on your side'}
        </p>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {recipe.audience === 'informational'
            ? 'The service asked us to slow down and we backed off. It is shown so the ' +
              'gap in the numbers is explained rather than unexplained. If it keeps ' +
              'recurring, our pacing is what needs adjusting — not anything of yours.'
            : 'This is a platform-side fault and has been raised with the development ' +
              'team. The evidence below is what they need — please contact support if ' +
              'it persists.'}
        </p>
      </div>
    )}

    {recipe.audience === 'client' && recipe.commands.length > 0 && (
      <div>
        <p className="mb-1 flex items-center gap-1.5 text-sm font-medium text-[var(--color-text-primary)]">
          <Terminal className="h-4 w-4" /> Run this
        </p>
        {recipe.commands.map((cmd, i) => (
          <pre
            key={i}
            className="mb-1 overflow-x-auto rounded bg-[var(--color-bg-subtle)] p-2 font-mono text-xs text-[var(--color-text-primary)]"
          >
            {cmd}
          </pre>
        ))}
        {recipe.placeholders.length > 0 && (
          // Stated, never guessed — a wrong id produces a confusing failure and leaves
          // the real gap in place with someone convinced it was handled.
          <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
            Replace before running: {recipe.placeholders.join(', ')}
          </p>
        )}
      </div>
    )}

    {recipe.portalSteps.length > 0 && recipe.audience === 'client' && (
      <div>
        <p className="mb-1 text-sm font-medium text-[var(--color-text-primary)]">
          {recipe.commands.length > 0 ? 'Then, in the portal' : 'In the portal'}
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--color-text-secondary)]">
          {recipe.portalSteps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </div>
    )}

    {/* 🛑 "No message" and "a message I don't recognise" are different claims, and only
        one can be true at a time. Conflating them printed "the platform did not return a
        message specific enough to name the exact fault" directly above an
        `AttributeError` that named the fault exactly — the screen contradicting the
        evidence it was itself displaying. */}
    {!recipe.fromEvidence && (recipe.commands.length > 0 || recipe.portalSteps.length > 0) && (
      <p className="text-xs text-[var(--color-text-secondary)]">
        {recipe.hasEvidence
          ? 'Inferred from the failure category — the message below is recorded but matches no known signature yet. Read it before changing anything; it may say more than this recipe can.'
          : 'Inferred from the failure category — the platform returned no message to work from. Verify before changing anything.'}
      </p>
    )}
  </div>
);

export const CollectionIntegrityPanel: React.FC<CollectionIntegrityPanelProps> = ({
  completeness,
  problems,
  problemCounts,
  streaks = [],
  onInspectProblem,
}) => {
  // Row-detail modals. A truncated cell is not an explanation, and "degraded" without
  // detail just relocates the question.
  const [stageDetail, setStageDetail] = useState<StageCompleteness | null>(null);
  const [problemDetail, setProblemDetail] = useState<CollectionProblem | null>(null);

  // 🛑 THREE STATES, NOT TWO. `full` = measured and complete. `degraded` = measured and
  //    incomplete. `uninstrumented` = never measured — which is NOT a degradation and must
  //    not be counted as one, or a tile reading "28 stages degraded" would send an
  //    operator hunting for 28 outages that do not exist. But it is not health either, so
  //    it stays visible; only genuinely clean stages get collapsed away.
  const notFull = completeness.filter((c) => c.disposition !== 'full');
  const degraded = completeness.filter((c) => c.disposition === 'degraded');
  const uninstrumented = completeness.filter((c) => c.disposition === 'uninstrumented');
  const authProblems = problems.filter(
    (p) => p.causeCategory === 'auth' || p.causeCategory === 'authorization',
  );
  // 🛑 Counted from the UNITS, not from the problem rows. A permission outage is visible
  //    in the arithmetic the moment it happens, whereas a problem row depends on the
  //    recorder having fired — and when that gate was wrong, this tile read 0 during a
  //    683-failure outage. Two independent paths to the same fact, so a defect in one
  //    cannot render the platform silent again.
  const authFailedUnits = completeness.reduce(
    (sum, c) =>
      sum +
      (c.units ?? []).reduce(
        (u, unit) => u + (unit.reasons?.auth ?? 0) + (unit.reasons?.authorization ?? 0),
        0,
      ),
    0,
  );
  // T159 item 29: when no stage counted units (the lane records none), the tile falls back to the open permission
  // problems, and says so; before, it showed the list's count with "no units refused".
  const unitsCounted = completeness.some((c) => (c.units ?? []).length > 0);
  const permissionProblems = problemCounts?.permission ?? authProblems.length;
  const bestStreak = streaks.reduce((m, s) => (s.streak > m ? s.streak : m), 0);
  // The coverage caveat is identical for every collector stage, so it is stated once here
  // instead of being repeated down the whole column.
  const anyIndeterminate = completeness.some((c) => c.expectedState === 'indeterminate');

  // 🛑 THE LINK BETWEEN THE TWO TABLES, MADE EXPLICIT.
  //    This screen printed four counts at four different grains — 686 failed units,
  //    5 degraded stages, 7 problems, 2 failed runs — and stated no relationship between
  //    any of them, so the only available reading was that they disagreed. They never
  //    disagreed: each problem belongs to exactly one stage, and every degraded stage
  //    either has problems or has a reason it does not. Joining them on `stage` is all it
  //    takes to turn four orphan numbers into one account.
  const problemsByStage = problems.reduce<Record<string, CollectionProblem[]>>((acc, p) => {
    (acc[p.stage] ||= []).push(p);
    return acc;
  }, {});
  const stagesWithProblems = Object.keys(problemsByStage).length;

  // Clean stages are collapsed by default when anything needs attention — but their COUNT
  // is always stated. Silently omitting them would make "nothing listed" mean both "all
  // clear" and "not shown", which is the ambiguity this panel exists to remove.
  const [showClean, setShowClean] = useState(false);
  // 🛑 THE DEFAULT VIEW IS WHAT NEEDS A HUMAN, i.e. the DEGRADED stages only.
  //
  //    It used to show everything that was not `full` — 26 of 30 rows on dev, because 21
  //    stages are uninstrumented. So the header said "5 stages did not complete" above a
  //    26-row table, and revealing the last 4 clean ones appended them below the fold:
  //    the operator clicked "Show 4 clean stages" and reported, correctly, that nothing
  //    changed. A control whose effect cannot be seen is indistinguishable from a broken
  //    one.
  //
  //    Nothing is silently omitted by this: the uninstrumented count is stated in the
  //    subtitle, in its own tile, and in the toggle's own label. Summarised is not hidden.
  const visibleStages = degraded.length > 0 && !showClean ? degraded : completeness;
  const hiddenCleanCount = completeness.length - visibleStages.length;
  const cleanCount = completeness.filter((c) => c.disposition === 'full').length;

  const completenessColumns: DataTableColumn<StageCompleteness>[] = [
    { key: 'stage', header: 'Stage', render: (r) => <span className="font-medium">{r.stage}</span> },
    {
      key: 'attempted',
      header: 'Attempted work',
      // 🛑 The unit is part of the number. Without it "100%" is a claim about an
      //    unnamed denominator, which is how a clean headline sat above 698 failures.
      render: (r) =>
        r.attemptedPct === null ? (
          <NotCollected reason="This stage recorded no attempts, so a success rate would be meaningless." />
        ) : (
          <div className="whitespace-nowrap">
            <span>{r.attemptedPct.toFixed(1)}%</span>
            <span className="ml-1.5 text-xs text-[var(--color-text-secondary)]">
              {r.succeededTotal}/{r.attemptedTotal} {unitLabel(r.primaryUnit, r.attemptedTotal ?? 0)}
            </span>
          </div>
        ),
    },
    {
      key: 'otherUnits',
      header: 'Other work',
      // The reconciliation, made visible: every unit this stage ALSO did, with its own
      // arithmetic. A failure here is why a 100% row can legitimately read "degraded".
      render: (r) => {
        // 🛑 No breakdown at all is NOT "only one unit". A run recorded before this
        //    column existed has nothing to say here, and answering "single unit of work"
        //    would be an assertion drawn from missing data — the same move as reporting
        //    a confident figure over an unknown denominator.
        if (!r.units || r.units.length === 0) {
          return (
            <NotCollected reason="This run recorded its totals only, so there is no breakdown by unit of work." />
          );
        }
        const others = r.units.filter((u) => !u.isPrimary);
        if (others.length === 0) {
          // Says what it means. An em-dash here reads as "unknown", when the recorded
          // fact is that this stage counts exactly one kind of thing.
          return <span className="text-xs text-[var(--color-text-secondary)]">single unit of work</span>;
        }
        const failing = others.filter((u) => u.failed > 0);
        return (
          <div className="whitespace-nowrap text-xs">
            {failing.length > 0 ? (
              <span className="text-[var(--color-status-danger)]">
                {failing.reduce((s, u) => s + u.failed, 0)} failed in {failing.length} of{' '}
                {plural(others.length, 'other unit')}
              </span>
            ) : (
              <span className="text-[var(--color-text-secondary)]">
                {plural(others.length, 'other unit')}, all clean
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'expected',
      header: 'Estate coverage',
      // 🛑 The REASON is not repeated down the column. Every collector stage is
      //    indeterminate for the identical reason, so printing that sentence on all 28
      //    rows filled the widest column with one repeated paragraph and buried the
      //    figures it sits next to. It is stated once above the table and in full in the
      //    row detail; the cell only has to say which of the two states this row is in.
      render: (r) =>
        r.expectedState === 'indeterminate' ? (
          <span className="whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
            not measured
          </span>
        ) : (
          <span>{r.expectedPct?.toFixed(1)}%</span>
        ),
    },
    {
      key: 'problems',
      header: 'Problems',
      // The join. A degraded row with 0 problems is a real and different state from a
      // degraded row with 3, and until this column existed the two looked identical.
      render: (r) => {
        const n = (problemsByStage[r.stage] ?? []).length;
        if (n === 0) {
          return <span className="text-xs text-[var(--color-text-secondary)]">none open</span>;
        }
        return <Badge variant="error">{n}</Badge>;
      },
    },
    {
      key: 'disposition',
      header: 'Outcome',
      render: (r) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Badge variant={dispositionVariant(r.disposition)}>{r.disposition}</Badge>
          {r.runningSince && <Badge variant="info">running now</Badge>}
        </span>
      ),
    },
  ];

  const problemColumns: DataTableColumn<CollectionProblem>[] = [
    {
      key: 'cause',
      header: 'Cause',
      render: (p) => <Badge variant={severityForCause(p.causeCategory)}>{p.causeCategory}</Badge>,
    },
    { key: 'scope', header: 'Affected scope', render: (p) => <span className="text-sm">{p.affectedScope}</span> },
    {
      key: 'count',
      header: 'Seen',
      // 🛑 The COUNT alone cannot answer "is this old or new?" — the question an
      //    operator actually has when facing an accumulated list. The age of the most
      //    recent occurrence can, so it travels with the count rather than hiding in
      //    a modal.
      render: (p) => (
        <div className="whitespace-nowrap text-sm">
          <span>{p.occurrenceCount}×</span>
          <span className="ml-1.5 text-xs text-[var(--color-text-secondary)]">
            {relativeAge(p.lastSeenAt)}
          </span>
        </div>
      ),
    },
    {
      key: 'diagnosis',
      // 🛑 The verdict, not the category. "auth" tells an operator nothing they can act
      //    on; "the secret is expired" tells them exactly where to go.
      header: 'Diagnosis',
      render: (p) => {
        const r = recipeFor(p);
        return (
          <div className="text-sm">
            <span className="text-[var(--color-text-primary)]">{r.verdict}</span>
            {!r.fromEvidence && (
              <span className="ml-1 text-xs text-[var(--color-text-secondary)]">(inferred)</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'fix',
      header: 'How to fix it',
      // The first command, verbatim. An operator should be able to see the shape of the
      // fix without opening anything; the full recipe is one click away.
      render: (p) => {
        const r = recipeFor(p);
        // Ours, not theirs — say so in the list too, or the row still reads as a chore
        // sitting in the customer's queue.
        if (r.audience !== 'client') {
          return (
            <span className="line-clamp-2 text-sm text-[var(--color-text-secondary)]">
              {r.audience === 'informational'
                ? 'Nothing to do — clears on its own.'
                : 'Nothing to change on your side — raised with the development team.'}
            </span>
          );
        }
        if (r.commands.length === 0) {
          return (
            <span className="line-clamp-2 text-sm text-[var(--color-text-secondary)]">
              {r.portalSteps[0] ?? 'No configuration change will help — see the detail.'}
            </span>
          );
        }
        return (
          <div className="max-w-md">
            <code className="block truncate font-mono text-xs text-[var(--color-text-secondary)]">
              {r.commands[0]}
            </code>
            {r.commands.length > 1 && (
              <span className="text-xs text-[var(--color-text-secondary)]">
                +{r.commands.length - 1} more step{r.commands.length > 2 ? 's' : ''}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* 🛑 EVERY TILE NAMES WHAT IT COUNTS. These three sat unlabelled above two tables
          holding numbers at three further grains, and nothing on the screen said whether
          "5", "686" and "7" were the same fact counted differently or three facts in
          disagreement. A count whose unit is unstated cannot be reconciled with anything,
          so it reads as a contradiction even when it is exactly right. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Stages degraded"
          value={String(degraded.length)}
          subtext={
            uninstrumented.length > 0
              ? `of ${completeness.length} reporting · ${uninstrumented.length} never measured`
              : `of ${plural(completeness.length, 'stage')} reporting`
          }
          icon={<AlertTriangle className="h-5 w-5" />}
        />
        <StatCard
          label="Permission failures"
          value={authFailedUnits > 0 ? String(authFailedUnits) : String(permissionProblems)}
          subtext={
            authFailedUnits > 0
              ? `units refused, across ${plural(stagesWithProblems, 'stage')}`
              : permissionProblems > 0
                ? `open permission problems${unitsCounted ? '' : ' · units not counted'}`
                : unitsCounted ? 'no units refused' : 'units not counted'
          }
          icon={<ShieldAlert className="h-5 w-5" />}
        />
        {/* FR-003b — sustained health, stated rather than implied. */}
        <StatCard
          label="Longest clean streak"
          value={bestStreak > 0 ? String(bestStreak) : '—'}
          subtext={bestStreak > 0 ? 'consecutive clean runs' : 'no clean run recorded yet'}
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
      </div>

      <Card
        title="Completeness by stage"
        // States both facts separately, because they call for different actions: a
        // degraded stage needs the failure fixing, an uninstrumented one needs a counter
        // added before anything can be claimed about it at all.
        subtitle={
          completeness.length === 0
            ? undefined
            : [
                degraded.length > 0
                  ? `${plural(degraded.length, 'stage')} of ${completeness.length} did not complete the work they attempted.`
                  : uninstrumented.length > 0
                    ? `${completeness.length - uninstrumented.length} of ${plural(completeness.length, 'stage')} completed everything they counted.`
                    : `All ${plural(completeness.length, 'stage')} completed the work they attempted.`,
                uninstrumented.length > 0
                  ? `${uninstrumented.length} recorded no counts at all, so nothing is known about them.`
                  : '',
              ]
                .filter(Boolean)
                .join(' ')
        }
        action={
          hiddenCleanCount > 0 || showClean ? (
            <button
              type="button"
              onClick={() => setShowClean((v) => !v)}
              className="whitespace-nowrap text-sm text-[var(--color-accent)] underline"
            >
              {showClean
                ? 'Show only stages needing attention'
                : `Show ${hiddenCleanCount} more (${uninstrumented.length} unmeasured, ${cleanCount} clean)`}
            </button>
          ) : undefined
        }
      >
        {completeness.length === 0 ? (
          <EmptyState
            icon={<HelpCircle className="h-6 w-6" />}
            title="No completeness recorded yet"
            // 🛑 Deliberately not "all healthy". Nothing measured is not the same as
            //    nothing wrong, and saying so is this feature's entire premise.
            message="No stage has reported completeness. That is not the same as every stage being healthy — it means nothing has been measured."
          />
        ) : (
          <>
            <p className="mb-2 text-xs text-[var(--color-text-secondary)]">
              Percentages measure the named unit only. A stage can be 100% on its primary
              unit and still degraded — click any row for the full per-unit breakdown.
              {anyIndeterminate &&
                ' Estate coverage is computed by the merge stage, so collectors show it as unmeasured.'}
            </p>
            <DataTable rows={visibleStages} columns={completenessColumns} rowKey={(r) => r.stage}
              minWidth="52rem" onRowClick={(r) => setStageDetail(r)} />
          </>
        )}
      </Card>

      <Card
        title="Problems"
        // 🛑 The count and how many stages it spans. Not "each belongs to a stage above": a
        //    merge's problem sits on a merge stage, and the table above lists collectors.
        subtitle={
          problems.length === 0
            ? undefined
            : problemCounts && problemCounts.total > problems.length
              ? `Showing ${problems.length} of ${plural(problemCounts.total, 'problem')} across ${plural(problemCounts.stages, 'stage')}.`
              : `${plural(problems.length, 'problem')} across ${plural(stagesWithProblems, 'stage')}.`
        }
      >
        {problems.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 className="h-6 w-6" />}
            title="No open problems"
            message="Every classified failure has cleared. Problems reappear automatically if the cause returns."
          />
        ) : (
          <DataTable rows={problems} columns={problemColumns} rowKey={(p) => p.id}
            minWidth="44rem" onRowClick={(p) => setProblemDetail(p)} />
        )}
      </Card>

      {/* Stage detail — every unit's own arithmetic, so the headline cannot be mistaken
          for a claim about work it never measured. */}
      <Modal
        isOpen={stageDetail !== null}
        onClose={() => setStageDetail(null)}
        title={stageDetail ? `Stage: ${stageDetail.stage}` : 'Stage'}
        size="lg"
      >
        {stageDetail && (
          <div className="space-y-4">
            {/* The direct answer to "is it 100% or not?" — stated before any number. */}
            {(stageDetail.failedAcrossUnits ?? 0) > 0 && (
              <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3 text-sm text-[var(--color-text-primary)]">
                <strong>{stageDetail.failedAcrossUnits} unit(s) failed in this run.</strong>{' '}
                The headline percentage covers only the{' '}
                <em>{unitLabel(stageDetail.primaryUnit)}</em> unit
                {failingSecondaryUnits(stageDetail).length > 0 && (
                  <> — the failures below happened in other units of work</>
                )}
                .
              </div>
            )}

            {stageDetail.runningSince && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                This stage is running now. These figures are from its last finished run.
              </p>
            )}

            <DescriptionList
              items={[
                {
                  label: 'Outcome',
                  value: (
                    // Same three-way reading as the table: uninstrumented is neutral, not
                    // a warning — nothing failed, nothing was measured.
                    <Badge variant={dispositionVariant(stageDetail.disposition)}>
                      {stageDetail.disposition}
                    </Badge>
                  ),
                },
                ...(stageDetail.disposition === 'degraded' || stageDetail.degradedReason
                  ? [{ label: 'Why degraded', value: stageDetail.degradedReason ?? 'No reason was recorded.' }]
                  : []),
                {
                  label: 'Estate coverage',
                  value:
                    stageDetail.expectedState === 'indeterminate'
                      ? `Unmeasured — ${whatIsMissing(stageDetail)}`
                      : `${stageDetail.expectedPct?.toFixed(1)}%`,
                },
              ]}
            />

            {/* Per-unit rows. Each line is internally consistent: its own attempted,
                its own succeeded, its own causes. */}
            <div>
              <p className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">
                Work by unit
              </p>
              {/* 🛑 A run recorded before this breakdown existed gets a reconstruction
                  from the headline columns, LABELLED as such. Presenting it as a real
                  breakdown would let a row that never recorded its secondary units look
                  identical to one that recorded them and found them clean. */}
              {(!stageDetail.units || stageDetail.units.length === 0) && (
                <p className="mb-2 text-xs text-[var(--color-text-secondary)]">
                  This run recorded its totals only, so there is no breakdown by unit of work.
                </p>
              )}
              <div className="space-y-2">
                {(stageDetail.units && stageDetail.units.length > 0
                  ? stageDetail.units
                  : [
                      {
                        unit: stageDetail.primaryUnit ?? 'item',
                        isPrimary: true,
                        attempted: stageDetail.attemptedTotal ?? 0,
                        succeeded: stageDetail.succeededTotal ?? 0,
                        failed: stageDetail.failedTotal ?? 0,
                        truncated: 0,
                        unaccounted: 0,
                        pct: stageDetail.attemptedPct,
                        reasons: stageDetail.failedByReason ?? {},
                      } as StageUnit,
                    ]
                ).map((u) => (
                  <div
                    key={u.unit}
                    className="rounded border border-[var(--color-border)] p-2.5"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-[var(--color-text-primary)]">
                        {unitLabel(u.unit)}
                        {u.isPrimary && (
                          <span className="ml-1.5 text-xs font-normal text-[var(--color-text-secondary)]">
                            (headline unit)
                          </span>
                        )}
                      </span>
                      <span className="text-sm text-[var(--color-text-secondary)]">
                        {u.succeeded}/{u.attempted} succeeded
                        {u.pct !== null && u.pct !== undefined && ` · ${u.pct.toFixed(1)}%`}
                        {u.failed > 0 && ` · ${u.failed} failed`}
                        {u.truncated > 0 && ` · ${u.truncated} truncated`}
                        {u.unaccounted > 0 && ` · ${u.unaccounted} unaccounted`}
                      </span>
                    </div>
                    {Object.keys(u.reasons ?? {}).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(u.reasons).map(([cause, n]) => (
                          <Badge key={cause} variant={severityForCause(cause)}>
                            {cause}: {n}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {/* The verbatim message — what turns a cause into an instruction. */}
                    {Object.entries(u.samples ?? {}).map(([cause, msgs]) =>
                      msgs.length ? (
                        <p
                          key={cause}
                          className="mt-1.5 break-words text-xs text-[var(--color-text-secondary)]"
                        >
                          <strong>{cause}:</strong> {msgs[0]}
                        </p>
                      ) : null,
                    )}
                    {u.worklistIncomplete && (
                      <p className="mt-1.5 text-xs text-[var(--color-text-secondary)]">
                        🛑 The work-list could not be fully enumerated
                        {u.worklistIncompleteReason ? `: ${u.worklistIncompleteReason}` : ''} — this
                        count is a floor, not a total.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 🛑 THE STAGE'S OWN PROBLEMS, IN THE STAGE'S OWN DETAIL. Answering "how does
                this correlate to everything else?" by making the reader hold two tables in
                their head and join on a column is not an answer. The join is done here.
                A degraded stage with NO problems is stated explicitly rather than left as
                an empty space — it means the failures were counted but never classified,
                which is itself worth knowing. */}
            <div>
              <p className="mb-1 text-sm font-medium text-[var(--color-text-primary)]">
                Problems attributed to this stage
              </p>
              <p className="mb-2 text-xs text-[var(--color-text-secondary)]">
                {/* 🛑 The first wording here was “With support”, which was reported as
                    unreadable within a day — it parses as “with support for …” rather than
                    “take this to support”, and it never said whose fault it was. The label
                    has to answer the reader's actual question, which is “is this mine?” */}
                Click a row for the details. “We’ll fix this” means the fault is on the
                platform side and has been recorded for the team — nothing in your
                environment needs changing. “Nothing to do” clears on its own.
              </p>
              {(problemsByStage[stageDetail.stage] ?? []).length === 0 ? (
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {stageDetail.disposition === 'full' && 'None — this stage completed the work it attempted.'}
                  {stageDetail.disposition === 'uninstrumented' && 'None. This run counted no work, so there is nothing to judge.'}
                  {stageDetail.disposition === 'degraded' &&
                    'None recorded. This stage did not finish all its work, but no failure here was classified into a problem, so there is no fix to suggest yet.'}
                  {stageDetail.disposition === 'indeterminate' && 'None recorded.'}
                </p>
              ) : (
                <div className="space-y-1.5">
                  {(problemsByStage[stageDetail.stage] ?? []).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setStageDetail(null);
                        setProblemDetail(p);
                      }}
                      className="flex w-full items-center gap-2 rounded border border-[var(--color-border)] p-2 text-left hover:bg-[var(--color-bg-subtle)]"
                    >
                      <Badge variant={severityForCause(p.causeCategory)}>{p.causeCategory}</Badge>
                      <span className="text-sm text-[var(--color-text-primary)]">
                        {recipeFor(p).verdict}
                      </span>
                      <span className="ml-auto whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
                        {p.occurrenceCount}× · {relativeAge(p.lastSeenAt)}
                      </span>
                      {/* 🛑 THE VERDICT ALONE LEAVES THE READER STRANDED. "a Conditional
                          Access policy is blocking this identity" is a correct diagnosis
                          and a dead end: it never says whether the reader is the person
                          who can act, and the steps that would tell them live one click
                          away behind a row with no affordance. Reported verbatim — "is
                          this something a user can influence? If so, it is not clear
                          how." So each row now answers that question where it is asked. */}
                      <span className="whitespace-nowrap text-xs font-medium text-[var(--color-accent)]">
                        {recipeFor(p).audience === 'client'
                          ? 'View the fix →'
                          : recipeFor(p).audience === 'informational'
                            ? 'Nothing to do'
                            : 'We’ll fix this →'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Problem detail — the full remediation and the evidence behind it. */}
      <Modal
        isOpen={problemDetail !== null}
        onClose={() => setProblemDetail(null)}
        title={problemDetail ? `${problemDetail.causeCategory} — ${problemDetail.affectedScope}` : 'Problem'}
        size="lg"
      >
        {problemDetail && (
          <div className="space-y-4">
            <DescriptionList
              items={[
                {
                  label: 'Cause',
                  value: (
                    <Badge variant={severityForCause(problemDetail.causeCategory)}>
                      {problemDetail.causeCategory}
                    </Badge>
                  ),
                },
                { label: 'Affected scope', value: problemDetail.affectedScope },
                { label: 'Identity', value: problemDetail.identityRef || 'Not attributed to a specific identity' },
                { label: 'Classification', value: problemDetail.classification },
                { label: 'Occurrences', value: `${problemDetail.occurrenceCount}×` },
                { label: 'First seen', value: new Date(problemDetail.firstSeenAt).toLocaleString() },
                { label: 'Last seen', value: new Date(problemDetail.lastSeenAt).toLocaleString() },
              ]}
            />
            <FixBlock recipe={recipeFor(problemDetail)} />
            {problemDetail.errorSamples && problemDetail.errorSamples.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-[var(--color-text-primary)]">
                  What the platform actually said
                </p>
                {problemDetail.errorSamples.map((s, i) => (
                  <p
                    key={i}
                    className="mb-1 break-words rounded bg-[var(--color-bg-subtle)] p-2 font-mono text-xs text-[var(--color-text-secondary)]"
                  >
                    {s}
                  </p>
                ))}
              </div>
            )}
            {/* Only when no signature matched — the note says none did. */}
            {problemDetail.causeCategory === 'unknown' && !recipeFor(problemDetail).fromEvidence && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                🛑 <strong>Why unknown?</strong> The error did not match any signature the
                classifier recognises. That is recorded honestly rather than guessed — a
                wrong classification would route a permission failure into the retry path.
                If the message above is a recurring shape, it belongs in the classifier.
              </p>
            )}
            {onInspectProblem && (
              <button
                type="button"
                onClick={() => onInspectProblem(problemDetail.id)}
                className="text-sm text-[var(--color-accent)] underline"
              >
                View all stored diagnostics
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CollectionIntegrityPanel;
